import { normalizeEntityType, normalizeRelTypeTokens } from "./SectionConfig";
import { getRelationshipMapping } from "./RelationshipTypeMapping";
import { createDevConsole } from "../../utils/devLogger";
import {
  ENRICHMENT_INCOMING_COMPOUND_SECTIONS,
  enrichmentIncomingCompoundRelMatches,
} from "../../utils/enrichmentRelationshipPersist";

const console = createDevConsole(false);

/**
 * Build mapping from relationship rules (from server) to section configuration
 * @param {Array} relationshipRules - Array of relationship rules from useRelationshipRules
 * @param {string} entityType - The entity type to get mappings for
 * @returns {Object} Mapping of section key to { relationshipTypes, direction, relatedEntityTypes }
 */
const buildMappingFromRules = (relationshipRules, entityType) => {
  if (
    !relationshipRules ||
    !Array.isArray(relationshipRules) ||
    relationshipRules.length === 0
  ) {
    return {};
  }

  const mapping = {};
  const normalizedEntityType = normalizeEntityTypeForMapping(entityType);

  relationshipRules.forEach((rule) => {
    const sectionKey = rule.key; // e.g., "provided_interfaces", "related_data_objects"

    if (!mapping[sectionKey]) {
      mapping[sectionKey] = {
        relationshipTypes: [],
        direction: rule.direction || "outgoing",
        relatedEntityTypes: [],
      };
    }

    // Add relationship type (can have multiple rules with same section key but different types)
    const relType = rule.relationshipType;
    if (relType && !mapping[sectionKey].relationshipTypes.includes(relType)) {
      mapping[sectionKey].relationshipTypes.push(relType);
    }

    // Add related entity type
    const relatedType = rule.relatedEntityType;
    if (
      relatedType &&
      !mapping[sectionKey].relatedEntityTypes.includes(relatedType)
    ) {
      mapping[sectionKey].relatedEntityTypes.push(relatedType);
    }
  });

  return mapping;
};

/**
 * Normalize entity type for mapping lookup (same as in RelationshipTypeMapping)
 */
const normalizeEntityTypeForMapping = (t) => {
  const s = String(t || "").toLowerCase();
  if (s === "organisation") return "organization";
  if (s === "org" || s === "org-unit" || s.includes("org unit"))
    return "organization";
  if (s.includes("applicationinterface") || s === "interface")
    return "interface";
  if (
    s.includes("application component") ||
    s.includes("applicationcomponent") ||
    s === "application"
  )
    return "application";
  if (
    s.includes("technologycomponent") ||
    s.includes("itcomponent") ||
    s.includes("it component") ||
    s === "technology-component"
  )
    return "technology-component";
  if (
    s.includes("dataobject") ||
    s.includes("data object") ||
    s === "data-object"
  )
    return "data-object";
  if (
    s.includes("businesscapability") ||
    s.includes("business capability") ||
    s === "business-capability"
  )
    return "business-capability";
  if (
    s.includes("businesscontext") ||
    s.includes("business context") ||
    s === "business-context"
  )
    return "business-context";
  if (s === "solution-component" || s.includes("solution component"))
    return "solution-component";
  if (s === "initiative") return "initiative";
  if (s === "organization" || s === "organisation") return "organization";
  if (s === "provider") return "provider";
  if (s === "objective" || s === "goal") return "objective";
  if (s === "platform") return "platform";
  return s;
};

/**
 * Get mapping for a specific entity type and section from relationship rules
 * @param {Array} relationshipRules - Array of relationship rules from useRelationshipRules
 * @param {string} entityType - The entity type
 * @param {string} sectionKey - The section key (e.g., "provided_interfaces")
 * @returns {Object|null} Mapping object or null if not found
 */
const getRelationshipMappingFromRules = (
  relationshipRules,
  entityType,
  sectionKey
) => {
  console.log(`🔍 [getRelationshipMappingFromRules] Called with:`, {
    hasRules: !!relationshipRules,
    isArray: Array.isArray(relationshipRules),
    length: relationshipRules?.length || 0,
    sectionKey,
    entityType,
    rulesType: typeof relationshipRules,
    rulesSample: relationshipRules?.slice(0, 2),
  });

  if (
    !relationshipRules ||
    !Array.isArray(relationshipRules) ||
    relationshipRules.length === 0
  ) {
    console.log(`🔍 [getRelationshipMappingFromRules] No rules provided:`, {
      hasRules: !!relationshipRules,
      isArray: Array.isArray(relationshipRules),
      length: relationshipRules?.length || 0,
      sectionKey,
      entityType,
    });
    return null;
  }

  // Find all rules that match this section key
  const matchingRules = relationshipRules.filter(
    (rule) => rule.key === sectionKey
  );

  if (matchingRules.length === 0) {
    console.log(
      `🔍 [getRelationshipMappingFromRules] No matching rules for section:`,
      {
        sectionKey,
        entityType,
        availableKeys: relationshipRules.map((r) => r.key),
        rulesCount: relationshipRules.length,
        sectionKeyLower: sectionKey?.toLowerCase(),
        availableKeysLower: relationshipRules.map((r) => r.key?.toLowerCase()),
        exactMatch: relationshipRules.some((r) => r.key === sectionKey),
        sampleRules: relationshipRules.slice(0, 5).map((r) => ({
          key: r.key,
          keyLower: r.key?.toLowerCase(),
          relationshipType: r.relationshipType,
          direction: r.direction,
          relatedEntityType: r.relatedEntityType,
        })),
      }
    );
    return null;
  }

  // Build mapping from matching rules
  const relationshipTypes = [];
  const relatedEntityTypes = [];
  let direction = matchingRules[0].direction || "outgoing";

  matchingRules.forEach((rule) => {
    if (
      rule.relationshipType &&
      !relationshipTypes.includes(rule.relationshipType)
    ) {
      relationshipTypes.push(rule.relationshipType);
    }
    if (
      rule.relatedEntityType &&
      !relatedEntityTypes.includes(rule.relatedEntityType)
    ) {
      relatedEntityTypes.push(rule.relatedEntityType);
    }
  });

  console.log(
    `🔍 [getRelationshipMappingFromRules] Built mapping for ${sectionKey}:`,
    {
      relationshipTypes,
      relationshipTypesCount: relationshipTypes.length,
      relationshipTypesStringified: relationshipTypes.map((t) =>
        JSON.stringify(t)
      ),
      direction,
      relatedEntityTypes,
      matchingRulesCount: matchingRules.length,
      sampleRule: matchingRules[0]
        ? {
            key: matchingRules[0].key,
            relationshipType: matchingRules[0].relationshipType,
            relationshipTypeStringified: JSON.stringify(
              matchingRules[0].relationshipType
            ),
            direction: matchingRules[0].direction,
            relatedEntityType: matchingRules[0].relatedEntityType,
          }
        : null,
      allRules: matchingRules.map((r) => ({
        key: r.key,
        relationshipType: r.relationshipType,
        relationshipTypeLower: String(r.relationshipType || "").toLowerCase(),
      })),
    }
  );

  return {
    relationshipTypes,
    direction,
    relatedEntityTypes,
  };
};

function dedupeSectionItems(merged) {
  const unique = [];
  const seen = new Set();
  for (const it of merged) {
    const dedupeKey =
      it.relationshipId != null && it.relationshipId !== ""
        ? `rel:${it.relationshipId}`
        : `ent:${it.id}`;
    if (!seen.has(dedupeKey)) {
      seen.add(dedupeKey);
      unique.push(it);
    }
  }
  return unique;
}

/** Resolve relationship row id when section item omitted it (legacy resolver paths). */
export function relationshipEdgeIdForSectionItem(
  sectionDef,
  currentEntityId,
  relationships,
  item
) {
  if (item?.relationshipId) return item.relationshipId;
  if (!item?.id || !currentEntityId || !Array.isArray(relationships)) {
    return null;
  }
  const cid = String(currentEntityId).trim().toLowerCase();
  const pid = String(item.id).trim().toLowerCase();
  const touchesPeer = (rel) => {
    const sid = String(rel.source_entity_id || "").trim().toLowerCase();
    const tid = String(rel.target_entity_id || "").trim().toLowerCase();
    return (
      (sid === cid && tid === pid) || (sid === pid && tid === cid)
    );
  };

  const enrichmentCfg = ENRICHMENT_INCOMING_COMPOUND_SECTIONS[sectionDef?.key];
  if (enrichmentCfg) {
    for (const rel of relationships) {
      if (!rel?.id || !touchesPeer(rel)) continue;
      if (
        enrichmentIncomingCompoundRelMatches(
          rel,
          sectionDef.key,
          cid,
          () => true
        )
      ) {
        return rel.id;
      }
    }
    return null;
  }

  for (const rel of relationships) {
    if (rel?.id && touchesPeer(rel)) return rel.id;
  }
  return null;
}

export const resolveSectionItems = ({
  currentEntity,
  relationships,
  entities,
  allEntities = {},
  fetchedEntities = {},
  sectionDef,
  entityForm,
  removedItems,
  relationshipRules = [], // Add relationship rules parameter
}) => {
  const formItems = entityForm[sectionDef.key] || [];
  const removed = removedItems[sectionDef.key] || [];

  // If no DB state yet or in create flow, just show form items
  if (!currentEntity || !relationships || !entities) {
    return formItems.filter((it) => !removed.includes(it.id));
  }

  const normalizeEntityId = (raw) => String(raw || "").trim().toLowerCase();
  const currentId = currentEntity.id;
  const currentIdNorm = normalizeEntityId(currentId);
  const entityIndex = new Map();
  const addEntityToIndex = (entity) => {
    if (!entity?.id) return;
    entityIndex.set(normalizeEntityId(entity.id), entity);
  };
  entities.forEach(addEntityToIndex);
  Object.entries(allEntities || {}).forEach(([id, e]) => {
    addEntityToIndex({
      id,
      ...e,
      type: e?.type || e?.entity_type,
      entity_type: e?.entity_type || e?.type,
    });
  });
  Object.values(fetchedEntities || {}).forEach(addEntityToIndex);
  const diagnostics = {
    excludedByUnknownType: 0,
    excludedByTypeMismatch: 0,
    excludedByMissingEntity: 0,
  };

  // Get mapping from relationship rules (server data) or fall back to hardcoded mapping or sectionDef
  const entityTypeForMapping = currentEntity.type || currentEntity.entity_type;
  let mapping = getRelationshipMappingFromRules(
    relationshipRules,
    entityTypeForMapping,
    sectionDef.key
  );
  let mappingSource = "server_rules";

  // Fallback to hardcoded mapping if server rules not available or didn't find a match
  if (!mapping) {
    mapping = getRelationshipMapping(entityTypeForMapping, sectionDef.key);
    if (mapping) {
      mappingSource = "hardcoded";
    }
  }

  if (mapping) {
    console.log(
      `✅ [resolveSectionItems] Found mapping for ${sectionDef.key}:`,
      {
        entityType: entityTypeForMapping,
        sectionKey: sectionDef.key,
        relationshipTypes: mapping.relationshipTypes,
        relationshipTypesLength: mapping.relationshipTypes?.length || 0,
        direction: mapping.direction,
        relatedEntityTypes: mapping.relatedEntityTypes,
        source: mappingSource,
      }
    );
  } else {
    console.log(
      `⚠️ [resolveSectionItems] No mapping found for ${sectionDef.key}, using sectionDef:`,
      {
        entityType: entityTypeForMapping,
        sectionKey: sectionDef.key,
        sectionDefRelationshipTypes: sectionDef.relationshipTypes,
        availableRules: relationshipRules?.map((r) => r.key) || [],
        rulesCount: relationshipRules?.length || 0,
      }
    );
  }

  // Use explicit mapping if available, otherwise fall back to sectionDef
  const relatedTypes = new Set(
    (mapping?.relatedEntityTypes || sectionDef.relatedEntityTypes || []).map(
      normalizeEntityType
    )
  );
  // Normalize relationship types - trim and create both original and lowercase versions
  const relationshipTypesArray =
    mapping?.relationshipTypes || sectionDef.relationshipTypes || [];

  // Debug: Log the raw array before processing
  if (
    sectionDef.key === "related_data_objects" ||
    sectionDef.key === "related_it_components"
  ) {
    console.log(
      `🔍 [DEBUG] Raw relationshipTypesArray for ${sectionDef.key}:`,
      {
        hasMapping: !!mapping,
        rawArray: relationshipTypesArray,
        rawArrayLength: relationshipTypesArray?.length || 0,
        rawArrayType: Array.isArray(relationshipTypesArray)
          ? "array"
          : typeof relationshipTypesArray,
        firstFew: relationshipTypesArray?.slice(0, 5),
      }
    );
  }

  const exactRelTypes = new Set(
    relationshipTypesArray.map((t) => String(t).trim())
  );
  // Also create a lowercase set for case-insensitive matching
  const exactRelTypesLower = new Set(
    relationshipTypesArray.map((t) => String(t).trim().toLowerCase())
  );

  // Debug logging for the first section
  if (
    sectionDef.key === "related_data_objects" ||
    sectionDef.key === "related_it_components"
  ) {
    const testReadsLower = "reads";
    const testReadsInSet = exactRelTypesLower.has(testReadsLower);
    const testReadsInArray =
      Array.from(exactRelTypesLower).includes(testReadsLower);
    const allValues = Array.from(exactRelTypesLower);
    console.log(`🔍 [DEBUG] ${sectionDef.key} mapping:`, {
      hasMapping: !!mapping,
      relationshipTypesArray: relationshipTypesArray,
      exactRelTypes: Array.from(exactRelTypes),
      exactRelTypesLower: allValues,
      arrayLength: relationshipTypesArray.length,
      setLength: exactRelTypes.size,
      lowerSetLength: exactRelTypesLower.size,
      testReads: testReadsInSet,
      testReadsDirect: testReadsInArray,
      testReadsValue: testReadsLower,
      allLowercaseValues: allValues.map((v) => `"${v}"`),
      firstFewValues: allValues.slice(0, 5),
    });
  }
  const direction = mapping?.direction || sectionDef.direction || "either";

  // For backward compatibility, also support matchTokens
  const tokensToMatch = new Set(
    (sectionDef.matchTokens || []).map((t) => String(t).toLowerCase())
  );
  function canonicalizeRelType(t) {
    const s = String(t || "")
      .toLowerCase()
      .trim();
    const groups = [
      {
        canon: "owns",
        aliases: [
          "owns",
          "owned",
          "owned by",
          "ownership",
          "governs",
          "governed by",
        ],
      },
      {
        canon: "supports",
        aliases: ["supports", "serves", "provides services to"],
      },
      { canon: "affects", aliases: ["affects", "impacts", "influences"] },
      {
        canon: "uses",
        aliases: [
          "uses",
          "utilizes",
          "consumes",
          "consumes from",
          "consumes service from",
        ],
      },
      {
        canon: "provides",
        aliases: [
          "provides",
          "offers",
          "supplies",
          "provided by",
          "is provided by",
        ],
      },
      {
        canon: "transfers",
        aliases: [
          "transfers",
          "accesses",
          "reads",
          "writes",
          "creates",
          "updates",
          "deletes",
        ],
      },
      { canon: "improves", aliases: ["improves", "enables", "supports"] },
      {
        canon: "linked",
        aliases: ["linked", "associated", "associated_with", "links"],
      },
      { canon: "realizes", aliases: ["realizes", "builds", "realizes/builds"] },
    ];
    for (const g of groups) {
      if (g.aliases.includes(s)) return g.canon;
    }
    return s;
  }
  // Canonical forms of exact types so aliases still match deterministically
  const exactCanonicalTypes = new Set(
    (sectionDef.relationshipTypes || [])
      .map((t) => canonicalizeRelType(t))
      .map((t) => String(t).toLowerCase())
  );

  const stem = (str) => {
    const s = String(str || "").toLowerCase();
    return s
      .replace(/ship$/, "")
      .replace(/ing$/, "")
      .replace(/ed$/, "")
      .replace(/s$/, "");
  };

  // Special handling for consumed_interfaces section (applications)
  // Keep this section endpoint-aware:
  // 1) app -> interface with consumes/utilizes (canonical)
  // 2) interface -> app (legacy modeling in some tenants)
  // 3) app -> interface with provides/exposes when interface name/description indicates
  //    this app is the target endpoint ("X to <this app> Interface")
  if (sectionDef.key === "consumed_interfaces" && currentEntity) {
    const currentType = normalizeEntityType(
      currentEntity?.type || currentEntity?.entity_type
    );
    if (currentType === "application") {
      // Case 1: App is target (incoming from interface) - regardless of relationship type
      // Helper function to find entity in multiple sources
      const findEntity = (entityId) => entityIndex.get(normalizeEntityId(entityId));

      const isInterfaceLike = (entity, hintedType) => {
        const normalizedHint = normalizeEntityType(hintedType || "");
        if (normalizedHint === "interface") return true;
        const t = normalizeEntityType(entity?.type || entity?.entity_type || "");
        if (t === "interface") return true;
        const rawType = String(entity?.type || entity?.entity_type || "").toLowerCase();
        if (rawType.includes("interface")) return true;
        const n = String(entity?.name || "").toLowerCase();
        return n.includes(" interface");
      };

      const appNameLower = String(currentEntity?.name || "").toLowerCase().trim();
      const looksLikeIncomingToCurrentApp = (interfaceEntity) => {
        const name = String(interfaceEntity?.name || "").toLowerCase();
        const desc = String(interfaceEntity?.description || "").toLowerCase();
        if (!appNameLower) return false;
        return name.includes(` to ${appNameLower}`) || desc.includes(` to ${appNameLower}`);
      };

      const incomingFromInterface = relationships.filter((rel) => {
        const isAppTarget = normalizeEntityId(rel.target_entity_id) === currentIdNorm;
        const sourceEntity = findEntity(rel.source_entity_id);
        const isInterfaceSource = isInterfaceLike(sourceEntity, rel.source_entity_type);
        return isAppTarget && isInterfaceSource;
      });

      // Case 2: App is source of "consumes"/"utilizes" relationship to interface
      const outgoingConsumes = relationships.filter((rel) => {
        const typeLower = (rel.type || "").toLowerCase();
        const isAppSource = normalizeEntityId(rel.source_entity_id) === currentIdNorm;
        const targetEntity = findEntity(rel.target_entity_id);
        const isInterfaceTarget = isInterfaceLike(targetEntity, rel.target_entity_type);
        return (
          (typeLower === "consumes" || typeLower === "utilizes") &&
          isAppSource &&
          isInterfaceTarget
        );
      });

      // Case 3: App is source of "provides"/"exposes", but the interface itself denotes
      // this app as the receiving endpoint (e.g. "SAP to FDH Interface").
      // This keeps consumed view correct even when rel type was authored as provides.
      const outgoingProvidesToCurrentAppEndpoint = relationships.filter((rel) => {
        const typeLower = (rel.type || "").toLowerCase();
        if (typeLower !== "provides" && typeLower !== "exposes") return false;
        if (normalizeEntityId(rel.source_entity_id) !== currentIdNorm) return false;
        const targetEntity = findEntity(rel.target_entity_id);
        if (!isInterfaceLike(targetEntity, rel.target_entity_type)) return false;
        return looksLikeIncomingToCurrentApp(targetEntity);
      });

      const specialMatches = [
        ...incomingFromInterface,
        ...outgoingConsumes,
        ...outgoingProvidesToCurrentAppEndpoint,
      ];

      // Map to entities
      const fromDb = specialMatches
        .map((rel) => {
          let interfaceId;
          if (normalizeEntityId(rel.target_entity_id) === currentIdNorm) {
            // Case 1: interface is source, app is target
            interfaceId = rel.source_entity_id;
          } else {
            // Case 2: app is source, interface is target
            interfaceId = rel.target_entity_id;
          }
          const resolvedEntity = findEntity(interfaceId);
          const iface = isInterfaceLike(
            resolvedEntity,
            normalizeEntityId(rel.target_entity_id) === currentIdNorm
              ? rel.source_entity_type
              : rel.target_entity_type
          )
            ? resolvedEntity
            : null;
          if (!iface) return null;
          return {
            ...iface,
            relationshipId: rel.id,
            activeFrom: rel.active_from,
            activeUntil: rel.active_until,
            description: rel.description,
            status: rel.status,
          };
        })
        .filter(Boolean);

      const merged = [...fromDb, ...formItems].filter(
        (item) => !removed.includes(item.id)
      );
      return dedupeSectionItems(merged);
    }
  }

  // Debug logging
  console.log(`🔍 [resolveSectionItems] Section: ${sectionDef.key}`, {
    currentId,
    relationshipsCount: relationships?.length || 0,
    relationshipsSample: relationships?.slice(0, 3).map((r) => ({
      id: r.id,
      type: r.type,
      source: r.source_entity_id,
      target: r.target_entity_id,
    })),
    entitiesCount: entities?.length || 0,
    allEntitiesCount: Object.keys(allEntities || {}).length,
    relatedTypes: Array.from(relatedTypes),
    exactRelTypes: Array.from(exactRelTypes),
    tokensToMatch: Array.from(tokensToMatch),
    direction: direction,
    sectionDef: {
      key: sectionDef.key,
      label: sectionDef.label,
      relationshipTypes: sectionDef.relationshipTypes,
      matchTokens: sectionDef.matchTokens,
      relatedEntityTypes: sectionDef.relatedEntityTypes,
    },
  });

  // First, filter relationships to only those involving the current entity
  const entityRelationships = relationships.filter((rel) => {
    return (
      normalizeEntityId(rel.source_entity_id) === currentIdNorm ||
      normalizeEntityId(rel.target_entity_id) === currentIdNorm
    );
  });

  // Get unique relationship types from the actual relationships
  const actualRelTypes = [
    ...new Set(
      entityRelationships
        .map((r) => String(r.type || "").trim())
        .filter(Boolean)
    ),
  ];

  // For related_data_objects, log all "reads" relationships
  if (sectionDef.key === "related_data_objects") {
    const readsRels = entityRelationships.filter(
      (r) =>
        String(r.type || "")
          .trim()
          .toLowerCase() === "reads"
    );
    console.log(
      `🔍 [resolveSectionItems] related_data_objects - Found ${readsRels.length} "reads" relationships:`,
      {
        readsRels: readsRels.map((r) => ({
          id: r.id,
          type: r.type,
          source: r.source_entity_id,
          target: r.target_entity_id,
        })),
      }
    );
  }

  console.log(
    `🔍 [resolveSectionItems] Filtered to entity relationships: ${entityRelationships.length} out of ${relationships.length}`,
    {
      sectionKey: sectionDef.key,
      currentId,
      actualRelationshipTypesInDB: actualRelTypes,
      actualRelationshipTypesCount: actualRelTypes.length,
      entityRelationshipsSample: entityRelationships.slice(0, 3).map((r) => ({
        id: r.id,
        type: r.type,
        source: r.source_entity_id,
        target: r.target_entity_id,
      })),
    }
  );

  // Enrichment compound incoming (org/initiative/platform): support canonical + compound
  // types and legacy previews that stored the application as source.
  const enrichmentIncomingCfg = ENRICHMENT_INCOMING_COMPOUND_SECTIONS[sectionDef.key];
  if (enrichmentIncomingCfg && currentEntity) {
    const findEntityLegacy = (entityId) =>
      entityIndex.get(normalizeEntityId(entityId));
    const peerLikeLegacy = (entityId, hintedType) => {
      const peer = findEntityLegacy(entityId);
      const t = normalizeEntityType(
        peer?.type || peer?.entity_type || hintedType || ""
      );
      return t === enrichmentIncomingCfg.peerType;
    };
    const legacyMatches = entityRelationships.filter((rel) =>
      enrichmentIncomingCompoundRelMatches(
        rel,
        sectionDef.key,
        currentIdNorm,
        peerLikeLegacy
      )
    );
    if (legacyMatches.length) {
      const formItems = (entityForm[sectionDef.key] || []).filter(
        (item) => !removed.includes(item.id)
      );
      const fromDb = legacyMatches
        .map((rel) => {
          const cid = normalizeEntityId(currentIdNorm);
          const peerId =
            normalizeEntityId(rel.target_entity_id) === cid
              ? rel.source_entity_id
              : rel.target_entity_id;
          const peer = findEntityLegacy(peerId);
          const hintedType = normalizeEntityType(
            normalizeEntityId(rel.target_entity_id) === cid
              ? rel.source_entity_type
              : rel.target_entity_type
          );
          const base = peer || {
            id: peerId,
            name: "Unknown",
            type: hintedType || enrichmentIncomingCfg.peerType,
            entity_type: hintedType || enrichmentIncomingCfg.peerType,
          };
          return {
            ...base,
            relationshipId: rel.id,
            activeFrom: rel.active_from,
            activeUntil: rel.active_until,
            description: rel.description,
            status: rel.status,
          };
        })
        .filter(Boolean);
      const merged = [...fromDb, ...formItems].filter(
        (item) => !removed.includes(item.id)
      );
      return dedupeSectionItems(merged);
    }
  }

  const matches = entityRelationships.filter((rel) => {
    const relType = String(rel.type || "").trim();
    const relTypeLower = relType.toLowerCase();

    // Simple exact match - check if relationship type is in the allowed list
    // Check both exact match and case-insensitive match
    let exactMatch = false;
    if (exactRelTypes.size > 0) {
      // Check exact match first (after trimming)
      exactMatch = exactRelTypes.has(relType);

      // Then check case-insensitive match using the lowercase set
      if (!exactMatch) {
        exactMatch = exactRelTypesLower.has(relTypeLower);
      }

      // Fallback: direct array check if Set.has() is failing for some reason
      if (!exactMatch) {
        exactMatch =
          Array.from(exactRelTypesLower).includes(relTypeLower) ||
          Array.from(exactRelTypes).some(
            (allowed) => String(allowed).trim().toLowerCase() === relTypeLower
          );
      }

      // Log successful matches for debugging
      if (exactMatch && entityRelationships.indexOf(rel) < 3) {
        console.log(`✅ [resolveSectionItems] Relationship type matched:`, {
          sectionKey: sectionDef.key,
          relType: rel.type,
          relTypeLower: relTypeLower,
          matched: true,
        });
      }
    } else {
      // No relationship type restriction - allow all
      exactMatch = true;
    }

    if (!exactMatch && exactRelTypes.size > 0) {
      // If we have explicit mapping, only use exact matches - no fuzzy matching
      if (mapping) {
        if (entityRelationships.indexOf(rel) < 5) {
          console.log(
            `🔍 [resolveSectionItems] Excluding relationship by type (exact mapping):`,
            {
              sectionKey: sectionDef.key,
              relId: rel.id,
              relType: rel.type,
              relTypeLower: relTypeLower,
              allowedTypes: Array.from(exactRelTypes),
              allowedTypesLower: Array.from(exactRelTypes).map((t) =>
                String(t).trim().toLowerCase()
              ),
              hasMapping: !!mapping,
              exactMatch: exactMatch,
              source: rel.source_entity_id,
              target: rel.target_entity_id,
              currentId: currentId,
            }
          );
          // Also log the actual values for easier debugging
          const allowedLowerArray = Array.from(exactRelTypesLower);
          const allowedOriginalArray = Array.from(exactRelTypes);
          console.log(
            `   Relationship type: "${rel.type}" (length: ${rel.type?.length}, lower: "${relTypeLower}")`
          );
          console.log(`   Allowed types (original):`, allowedOriginalArray);
          console.log(`   Allowed types (lowercase):`, allowedLowerArray);
          console.log(
            `   Allowed types (lowercase) STRINGIFIED:`,
            allowedLowerArray.map((v) => JSON.stringify(v))
          );
          // Log the actual values as a comma-separated string for easy reading
          console.log(
            `   Allowed types (lowercase) VALUES: "${allowedLowerArray.join('", "')}"`
          );
          console.log(`   Looking for: "${relTypeLower}"`);
          const directCheck = allowedLowerArray.some((v) => v === relTypeLower);
          const directCheck2 = allowedLowerArray.includes(relTypeLower);
          console.log(
            `   Match check: exactRelTypes.has("${relType}") = ${exactRelTypes.has(relType)}, exactRelTypesLower.has("${relTypeLower}") = ${exactRelTypesLower.has(relTypeLower)}, directArrayCheck = ${directCheck}, directArrayCheck2 = ${directCheck2}`
          );
          // Show the actual values clearly
          console.log(`   Array contents check:`, {
            arrayLength: allowedLowerArray.length,
            ALL_VALUES_AS_STRINGS: allowedLowerArray
              .map((v) => `"${v}"`)
              .join(", "),
            ALL_VALUES_RAW: allowedLowerArray,
            lookingFor: `"${relTypeLower}"`,
            lookingForLength: relTypeLower.length,
            foundIndex: allowedLowerArray.indexOf(relTypeLower),
            eachValue: allowedLowerArray.map((v, i) => ({
              index: i,
              value: `"${v}"`,
              valueLength: v.length,
              matches: v === relTypeLower,
              typeMatch: typeof v === typeof relTypeLower,
            })),
          });
          // Also log what the server rule actually contains
          if (mapping && mapping.relationshipTypes) {
            console.log(
              `   Server rule relationshipTypes:`,
              mapping.relationshipTypes
            );
            console.log(
              `   Server rule relationshipTypes (lowercase):`,
              mapping.relationshipTypes.map((t) =>
                String(t).trim().toLowerCase()
              )
            );
            console.log(
              `   Server rule relationshipTypes VALUES: "${mapping.relationshipTypes.map((t) => String(t).trim()).join('", "')}"`
            );
            console.log(
              `   Server rule relationshipTypes (lowercase) VALUES: "${mapping.relationshipTypes.map((t) => String(t).trim().toLowerCase()).join('", "')}"`
            );
          }
        }
        return false;
      }

      // Fallback to token matching only if no explicit mapping (backward compatibility)
      const tokensToCheck =
        tokensToMatch.size > 0
          ? tokensToMatch
          : new Set(Array.from(exactRelTypes).map((t) => t.toLowerCase()));

      const relTokens = normalizeRelTypeTokens(rel.type);
      const hasToken = relTokens.some((t) => {
        const ts = stem(t);
        for (const mt of tokensToCheck) {
          const ms = stem(mt);
          if (
            t === mt ||
            t.includes(mt) ||
            mt.includes(t) ||
            ts === ms ||
            ts.startsWith(ms) ||
            ms.startsWith(ts)
          ) {
            return true;
          }
        }
        return false;
      });

      if (!hasToken) {
        if (entityRelationships.indexOf(rel) < 3) {
          console.log(
            `🔍 [resolveSectionItems] Excluding relationship by type:`,
            {
              sectionKey: sectionDef.key,
              relId: rel.id,
              relType: rel.type,
              allowedTypes: Array.from(exactRelTypes),
              tokensToMatch: Array.from(tokensToMatch),
            }
          );
        }
        return false;
      }
    }

    // Helper function to find entity in multiple sources
    const findEntity = (entityId) => entityIndex.get(normalizeEntityId(entityId));

    const resolveOtherTypeFromRel = (isOutgoingDirection) => {
      const hintedType = isOutgoingDirection
        ? rel.target_entity_type
        : rel.source_entity_type;
      return normalizeEntityType(hintedType || "");
    };

    if (direction === "outgoing") {
      if (normalizeEntityId(rel.source_entity_id) !== currentIdNorm) {
        if (entityRelationships.indexOf(rel) < 3) {
          console.log(
            `🔍 [resolveSectionItems] Excluding by direction (outgoing):`,
            {
              sectionKey: sectionDef.key,
              relId: rel.id,
              source: rel.source_entity_id,
              currentId,
            }
          );
        }
        return false;
      }
      const otherId = rel.target_entity_id;
      const other = findEntity(otherId);
      const hintedType = resolveOtherTypeFromRel(true);
      const otherType = normalizeEntityType(
        other?.type || other?.entity_type || hintedType
      );
      if (!other && !otherType) {
        // Entity/type not found - only include when section has no type restriction.
        if (relatedTypes.size === 0) return true;
        diagnostics.excludedByMissingEntity += 1;
        if (entityRelationships.indexOf(rel) < 3) {
          console.log(
            `🔍 [resolveSectionItems] Entity not found for outgoing relationship:`,
            {
              sectionKey: sectionDef.key,
              relId: rel.id,
              otherId,
              allowedTypes: Array.from(relatedTypes),
            }
          );
        }
        return false;
      }
      const ok =
        relatedTypes.size === 0 ||
        (otherType &&
          otherType !== "unknown" &&
          otherType !== "item" &&
          relatedTypes.has(otherType));
      if (!ok && relatedTypes.size > 0) {
        if (!otherType || otherType === "unknown" || otherType === "item") {
          diagnostics.excludedByUnknownType += 1;
        } else {
          diagnostics.excludedByTypeMismatch += 1;
        }
      }
      if (entityRelationships.indexOf(rel) < 3) {
        console.log(
          `🔍 [resolveSectionItems] Direction ok (outgoing); entity type check:`,
          {
            sectionKey: sectionDef.key,
            relId: rel.id,
            otherId,
            otherType,
            otherRawType: other?.type || other?.entity_type,
            allowedTypes: Array.from(relatedTypes),
            include: ok,
            otherFound: !!other,
          }
        );
      }
      return ok;
    } else if (direction === "incoming") {
      if (normalizeEntityId(rel.target_entity_id) !== currentIdNorm) {
        if (entityRelationships.indexOf(rel) < 3) {
          console.log(
            `🔍 [resolveSectionItems] Excluding by direction (incoming):`,
            {
              sectionKey: sectionDef.key,
              relId: rel.id,
              target: rel.target_entity_id,
              currentId,
            }
          );
        }
        return false;
      }
      const otherId = rel.source_entity_id;
      const other = findEntity(otherId);
      const hintedType = resolveOtherTypeFromRel(false);
      const otherType = normalizeEntityType(
        other?.type || other?.entity_type || hintedType
      );
      if (!other && !otherType) {
        // Entity/type not found - only include when section has no type restriction.
        if (relatedTypes.size === 0) return true;
        diagnostics.excludedByMissingEntity += 1;
        if (entityRelationships.indexOf(rel) < 3) {
          console.log(
            `🔍 [resolveSectionItems] Entity not found for incoming relationship:`,
            {
              sectionKey: sectionDef.key,
              relId: rel.id,
              otherId,
              allowedTypes: Array.from(relatedTypes),
            }
          );
        }
        return false;
      }
      const ok =
        relatedTypes.size === 0 ||
        (otherType &&
          otherType !== "unknown" &&
          otherType !== "item" &&
          relatedTypes.has(otherType));
      if (!ok && relatedTypes.size > 0) {
        if (!otherType || otherType === "unknown" || otherType === "item") {
          diagnostics.excludedByUnknownType += 1;
        } else {
          diagnostics.excludedByTypeMismatch += 1;
        }
      }
      if (entityRelationships.indexOf(rel) < 3) {
        console.log(
          `🔍 [resolveSectionItems] Direction ok (incoming); entity type check:`,
          {
            sectionKey: sectionDef.key,
            relId: rel.id,
            otherId,
            otherType,
            otherRawType: other?.type || other?.entity_type,
            allowedTypes: Array.from(relatedTypes),
            include: ok,
            otherFound: !!other,
          }
        );
      }
      return ok;
    } else {
      // either direction
      let otherId, otherEntity, otherType;
      if (normalizeEntityId(rel.source_entity_id) === currentIdNorm) {
        otherId = rel.target_entity_id;
        otherEntity = findEntity(otherId);
        const hintedType = resolveOtherTypeFromRel(true);
        otherType = normalizeEntityType(
          otherEntity?.type || otherEntity?.entity_type || hintedType
        );
        if (!otherEntity && !otherType) {
          if (relatedTypes.size === 0) return true;
          diagnostics.excludedByMissingEntity += 1;
          return false;
        }
        const ok =
          relatedTypes.size === 0 ||
          (otherType &&
            otherType !== "unknown" &&
            otherType !== "item" &&
            relatedTypes.has(otherType));
        if (!ok && relatedTypes.size > 0) {
          if (!otherType || otherType === "unknown" || otherType === "item") {
            diagnostics.excludedByUnknownType += 1;
          } else {
            diagnostics.excludedByTypeMismatch += 1;
          }
        }
        if (sectionDef.key === "organization_owns_initiative_reverse") {
          console.log("🔎 [OWNERSHIP] Either-direction source=current check:", {
            relId: rel.id,
            otherId,
            otherType,
            include: ok,
          });
        }
        if (ok) return true;
      }
      if (normalizeEntityId(rel.target_entity_id) === currentIdNorm) {
        otherId = rel.source_entity_id;
        otherEntity = findEntity(otherId);
        const hintedType = resolveOtherTypeFromRel(false);
        otherType = normalizeEntityType(
          otherEntity?.type || otherEntity?.entity_type || hintedType
        );
        if (!otherEntity && !otherType) {
          if (relatedTypes.size === 0) return true;
          diagnostics.excludedByMissingEntity += 1;
          return false;
        }
        const ok =
          relatedTypes.size === 0 ||
          (otherType &&
            otherType !== "unknown" &&
            otherType !== "item" &&
            relatedTypes.has(otherType));
        if (!ok && relatedTypes.size > 0) {
          if (!otherType || otherType === "unknown" || otherType === "item") {
            diagnostics.excludedByUnknownType += 1;
          } else {
            diagnostics.excludedByTypeMismatch += 1;
          }
        }
        if (sectionDef.key === "organization_owns_initiative_reverse") {
          console.log("🔎 [OWNERSHIP] Either-direction target=current check:", {
            relId: rel.id,
            otherId,
            otherType,
            include: ok,
          });
        }
        if (ok) return true;
      }
      return false;
    }
  });

  console.log(`🔍 [resolveSectionItems] Matches found: ${matches.length}`, {
    sectionKey: sectionDef.key,
    matchesSample: matches.slice(0, 3).map((m) => ({
      id: m.id,
      type: m.type,
      source: m.source_entity_id,
      target: m.target_entity_id,
    })),
  });
  if (
    diagnostics.excludedByUnknownType > 0 ||
    diagnostics.excludedByTypeMismatch > 0 ||
    diagnostics.excludedByMissingEntity > 0
  ) {
    console.warn(
      `🧭 [resolveSectionItems] Section "${sectionDef.key}" diagnostics:`,
      {
        excludedByUnknownType: diagnostics.excludedByUnknownType,
        excludedByTypeMismatch: diagnostics.excludedByTypeMismatch,
        excludedByMissingEntity: diagnostics.excludedByMissingEntity,
      }
    );
  }

  const fromDb = matches
    .map((rel) => {
      let otherId;
      if (direction === "outgoing") {
        otherId = rel.target_entity_id;
      } else if (direction === "incoming") {
        otherId = rel.source_entity_id;
      } else {
        // either: pick the non-current end
        otherId =
          normalizeEntityId(rel.source_entity_id) === currentIdNorm
            ? rel.target_entity_id
            : rel.source_entity_id;
      }
      // Helper function to find entity in multiple sources
      const findEntity = (entityId) => {
        const entity = entityIndex.get(normalizeEntityId(entityId));
        if (
          !entity &&
          sectionDef.key === "related_data_objects" &&
          matches.indexOf(rel) < 3
        ) {
          console.warn(
            `🔍 [resolveSectionItems] Entity not found for related_data_objects: ${entityId}`,
            {
              sectionKey: sectionDef.key,
              relId: rel.id,
              relType: rel.type,
              checkedEntities: entities.length,
              checkedAllEntities: Object.keys(allEntities || {}).length,
              checkedFetched: Object.keys(fetchedEntities || {}).length,
              entitiesSample: entities
                .slice(0, 3)
                .map((e) => ({ id: e.id, name: e.name, type: e.type })),
            }
          );
        }
        return entity;
      };

      const other = findEntity(otherId);
      const hintedEndpointType = normalizeEntityType(
        direction === "outgoing"
          ? rel.target_entity_type
          : rel.source_entity_type
      );
      let base = other || {
        id: otherId,
        name: "Unknown",
        type: hintedEndpointType || "unknown",
        entity_type: hintedEndpointType || "unknown",
      };
      // Heuristic: if expected type is 'interface' but the matched entity type isn't,
      // and the name clearly denotes an Interface, coerce the display type to interface.
      const expected =
        (sectionDef.relatedEntityTypes && sectionDef.relatedEntityTypes[0]) ||
        "";
      const normalizedBaseType = normalizeEntityType(
        base.type || base.entity_type
      );
      if (expected === "interface" && normalizedBaseType !== "interface") {
        const n = String(base.name || "").toLowerCase();
        if (n.includes(" interface")) {
          base = { ...base, type: "interface", entity_type: "interface" };
        }
      }
      return {
        ...base,
        /** Edge row id for DELETE /api/relationships/:id (entity id stays on `id`). */
        relationshipId: rel.id,
        activeFrom: rel.active_from,
        activeUntil: rel.active_until,
        description: rel.description,
        status: rel.status,
      };
    })
    .filter(Boolean);

  console.log(`🔍 [resolveSectionItems] Results for ${sectionDef.key}:`, {
    matchesCount: matches.length,
    fromDbCount: fromDb.length,
    formItemsCount: formItems.length,
    removedCount: removed.length,
    fromDbSample: fromDb
      .slice(0, 3)
      .map((e) => ({ id: e.id, name: e.name, type: e.type || e.entity_type })),
  });

  const merged = [...fromDb, ...formItems].filter(
    (item) => !removed.includes(item.id)
  );
  if (sectionDef.key === "organization_owns_initiative_reverse") {
    console.log("🔎 [OWNERSHIP] Summary:", {
      currentId,
      sectionDef,
      matchesCount: matches.length,
      fromDbCount: fromDb.length,
      formCount: formItems.length,
      removedCount: removed.length,
    });
  }
  const unique = dedupeSectionItems(merged);

  console.log(
    `🔍 [resolveSectionItems] Final result for ${sectionDef.key}: ${unique.length} items`
  );

  return unique;
};
